import { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { TranscriptionExtended } from 'flow/types/CallRecord';
import { CallRecordKey, TicketKey } from 'flow/types/EntityKeys';
import { useLegacyTranscription } from 'flow/hooks/api/useLegacyTranscription';
import useHasObservedGeneratingTranscription from 'flow/hooks/useHasObservedGeneratingTranscription';
import {
    selectHasExtendedTranscriptionInfo,
    selectCallRecordIsPersonalRecording,
    selectExtendedTranscriptionIsPending,
} from 'flow/selectors/call-records-selectors';
import { PageType } from 'flow/components/call-history-v2/types';
import SectionRecordingControls from './SectionRecordingControls';
import SectionTranscriptionResults from './SectionTranscriptionResults';

interface Props {
    callRecordKey: CallRecordKey;
    extendedTranscription?: TranscriptionExtended;
    // On the PBX page (and tickets) we don't want to expose recordings/transcriptions that the API only
    // returns to their owner. Even though there's no actual leakage, surfacing them would make the
    // viewer wonder whether other people can see their personal content.
    hidePersonalContent?: boolean;
    pageType?: PageType;
    // The ticket the call belongs to, when rendered in the ticket feed. Enables the ticket
    // actions ("…" menu) on the summary card.
    ticketKey?: TicketKey;
}

/**
 * The recording + transcription block of a call record (audio playback, generate/show transcript,
 * summary and suggested actions), shared by the call record page's log entry and the ticket call
 * card so both render and behave identically. The extended transcription is fetched by the caller and
 * passed in.
 */
const SectionRecordingAndTranscription = ({
    callRecordKey,
    extendedTranscription,
    hidePersonalContent,
    pageType,
    ticketKey,
}: Props) => {
    // The summary card's ticket "…" menu (suggest tags, copy summary) is available for the whole of
    // a ticket's lifetime. Note the transcription's own `tags` are deliberately NOT surfaced here:
    // suggested tags appear only in the tags dialog, after the user has asked for them via "suggest
    // tags", so we never put AI suggestions in front of someone who didn't request them.
    const contextSupportsTicketActions = pageType === 'tickets';
    const isPersonalRecording = useAppSelector((state) =>
        selectCallRecordIsPersonalRecording(state, callRecordKey),
    );
    const transcriptionIsPending = useAppSelector((state) =>
        selectExtendedTranscriptionIsPending(state, callRecordKey),
    );
    const hasExtendedTranscriptionInfo = useAppSelector((state) =>
        selectHasExtendedTranscriptionInfo(state, callRecordKey),
    );

    const recordingIsHidden = hidePersonalContent && isPersonalRecording;

    // Local flag for the brief period between the user clicking "Transcribe call"
    // and the server confirming via transcriptionIsPending in Redux.
    const [
        awaitingTranscriptionConfirmation,
        setAwaitingTranscriptionConfirmation,
    ] = useState(false);

    const onAwaitTranscriptionConfirmation = useCallback(() => {
        setAwaitingTranscriptionConfirmation(true);
    }, []);

    // Reset when Redux takes over or when transcription data arrives
    useEffect(() => {
        if (
            awaitingTranscriptionConfirmation &&
            (transcriptionIsPending || extendedTranscription)
        ) {
            setAwaitingTranscriptionConfirmation(false);
        }
    }, [
        awaitingTranscriptionConfirmation,
        transcriptionIsPending,
        extendedTranscription,
    ]);

    const transcriptionLegacy = useLegacyTranscription(
        callRecordKey,
        !hasExtendedTranscriptionInfo && !recordingIsHidden,
    );

    const hasObservedGenerating = useHasObservedGeneratingTranscription(
        callRecordKey,
        transcriptionIsPending ||
            awaitingTranscriptionConfirmation ||
            transcriptionLegacy.isGenerating,
    );

    return (
        <>
            <SectionRecordingControls
                callRecordKey={callRecordKey}
                extendedTranscription={extendedTranscription}
                transcriptionIsPending={transcriptionIsPending}
                onAwaitTranscriptionConfirmation={
                    onAwaitTranscriptionConfirmation
                }
                legacyTranscript={transcriptionLegacy.transcript}
                legacySummary={transcriptionLegacy.summary}
                isGeneratingLegacyTranscription={
                    transcriptionLegacy.isGenerating
                }
                isAttemptingToFetchLegacyTranscription={
                    transcriptionLegacy.isAttemptingToFetch
                }
                onLegacyTranscribe={transcriptionLegacy.transcribe}
                hidePersonalContent={hidePersonalContent}
                pageType={pageType}
            />
            <SectionTranscriptionResults
                extendedTranscription={extendedTranscription}
                transcriptionIsPending={transcriptionIsPending}
                awaitingTranscriptionConfirmation={
                    awaitingTranscriptionConfirmation
                }
                callRecordKey={callRecordKey}
                legacySummary={transcriptionLegacy.summary}
                isGeneratingLegacyTranscription={
                    transcriptionLegacy.isGenerating
                }
                animateSummary={hasObservedGenerating}
                hidePersonalContent={hidePersonalContent}
                displayTicketActions={contextSupportsTicketActions}
                ticketKey={ticketKey}
            />
        </>
    );
};

export default SectionRecordingAndTranscription;
